import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaShoppingCart, FaArrowLeft, FaLeaf } from 'react-icons/fa';
import { microgreensData } from '../data/products';
import './ProductDetail.css';

const ProductDetail = ({ addToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = microgreensData.find(p => p.id === parseInt(id));

  if (!product) {
    return (
      <div className="product-detail-container">
        <div className="product-not-found">
          <h2>Product Not Found</h2>
          <p>Sorry, we couldn't find the product you're looking for.</p>
          <Link to="/microgreens" className="back-link">
            <FaArrowLeft /> Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    const cartItem = {
      ...product,
      id: Date.now() + Math.random(),
      totalPrice: product.price
    };
    addToCart(cartItem);
  };

  return (
    <div className="product-detail-container">
      <Helmet>
        <title>{product.name} | Folsom Microgreens</title>
        <meta 
          name="description" 
          content={product.description}
        />
      </Helmet>

      <div className="product-detail">
        {/* Back Button */}
        <Link to="/microgreens" className="back-button">
          <FaArrowLeft /> Back to Products
        </Link>

        <div className="product-detail-content">
          {/* Product Image */}
          <div className="product-image-section">
            <img 
              src={`/images/${product.image}`} 
              alt={`${product.name} microgreens`}
              className="product-detail-image"
            />
            {!product.isAvailable && (
              <div className="availability-badge unavailable">
                Currently Unavailable
              </div>
            )}
            {product.isAvailable && (
              <div className="availability-badge available">
                Available Now
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className="product-info-section">
            <div className="product-header">
              <FaLeaf className="product-icon" />
              <h1 className="product-title">{product.name}</h1>
            </div>

            <p className="product-description">{product.description}</p>

            <div className="product-pricing">
              <span className="product-price">${product.price.toFixed(2)}</span>
            </div>

            {product.isAvailable && (
              <button 
                className="add-to-cart-button"
                onClick={handleAddToCart}
                aria-label={`Add ${product.name} to cart`}
              >
                <FaShoppingCart className="cart-icon" />
                Add to Cart
              </button>
            )}

            {!product.isAvailable && (
              <div className="unavailable-message">
                <p>This product is currently unavailable. Check back soon!</p>
                <Link to="/microgreens" className="browse-link">
                  Browse Available Products
                </Link>
              </div>
            )}

            {/* Detailed Information Sections */}
            <div className="product-details">
              <div className="detail-section">
                <h3 className="detail-title">
                  <FaLeaf className="section-icon" />
                  Nutrition Benefits
                </h3>
                <p className="detail-content">{product.nutrition}</p>
              </div>

              <div className="detail-section">
                <h3 className="detail-title">
                  <FaLeaf className="section-icon" />
                  Usage & Preparation
                </h3>
                <p className="detail-content">{product.usage}</p>
              </div>

              {product.chefPairing && (
                <div className="detail-section">
                  <h3 className="detail-title">
                    <FaLeaf className="section-icon" />
                    For Chefs
                  </h3>
                  <p className="detail-content">{product.chefPairing}</p>
                  <p className="detail-content" style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                    <a
                      href="/chefs"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate('/chefs');
                      }}
                    >
                      See the chef page →
                    </a>
                  </p>
                </div>
              )}
            </div>

            {/* Additional Information */}
            <div className="additional-info">
              <h3>Why Choose Our Microgreens?</h3>
              <ul className="benefits-list">
                <li>Locally grown in Folsom, California</li>
                <li>Harvested fresh for maximum nutrition</li>
                <li>No pesticides or harmful chemicals</li>
                <li>Packed with vitamins and antioxidants</li>
                <li>Perfect for health-conscious food lovers</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Related Products or Call to Action */}
        <div className="product-cta">
          <p>Looking for more variety?</p>
          <Link to="/microgreens" className="cta-button">
            View All Microgreens
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
